import React, { useState, useEffect } from 'react';
import FourPillars from '../components/FourPillars';

const ExcessiveInventoryPage = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [activeSection, setActiveSection] = useState('');

  const slideBgStyle = {
    backgroundImage: 'linear-gradient(120deg, rgba(15,23,42,0.9), rgba(132,204,22,0.75)), url(/store.png)',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
  };

  const StepIcon = ({ step }) => {
    const iconSize = 140;
    const common = {
      width: iconSize,
      height: iconSize,
      viewBox: '0 0 140 140',
      fill: 'none',
    };
    
    switch (step) {
      case 1:
        return (
          <svg {...common}>
            <rect x="20" y="20" width="100" height="100" rx="12" stroke="currentColor" strokeWidth="4" fill="none"/>
            <path d="M20 20 L30 30 M120 20 L110 30 M20 120 L30 110 M120 120 L110 110" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <rect x="40" y="40" width="60" height="60" rx="4" stroke="currentColor" strokeWidth="3" fill="none"/>
            <path d="M50 55h40M50 70h40M50 85h30" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <circle cx="90" cy="85" r="6" fill="currentColor"/>
          </svg>
        );
      case 2:
        return (
          <svg {...common}>
            <rect x="20" y="20" width="100" height="100" rx="12" stroke="currentColor" strokeWidth="4" fill="none"/>
            <path d="M20 20 L30 30 M120 20 L110 30 M20 120 L30 110 M120 120 L110 110" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <rect x="35" y="50" width="70" height="40" rx="4" stroke="currentColor" strokeWidth="3" fill="none"/>
            <path d="M45 65h50M45 80h50" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <circle cx="50" cy="95" r="6" fill="currentColor"/>
            <circle cx="70" cy="95" r="6" fill="currentColor"/>
            <circle cx="90" cy="95" r="6" fill="currentColor"/>
          </svg>
        );
      case 3:
        return (
          <svg {...common}>
            <rect x="20" y="20" width="100" height="100" rx="12" stroke="currentColor" strokeWidth="4" fill="none"/>
            <path d="M20 20 L30 30 M120 20 L110 30 M20 120 L30 110 M120 120 L110 110" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <circle cx="45" cy="50" r="12" stroke="currentColor" strokeWidth="3" fill="none"/>
            <circle cx="95" cy="50" r="12" stroke="currentColor" strokeWidth="3" fill="none"/>
            <circle cx="45" cy="90" r="12" stroke="currentColor" strokeWidth="3" fill="none"/>
            <circle cx="95" cy="90" r="12" stroke="currentColor" strokeWidth="3" fill="none"/>
            <path d="M57 50h36M45 62v36M95 62v36M57 90h36" stroke="currentColor" strokeWidth="3"/>
            <circle cx="70" cy="70" r="8" fill="currentColor"/>
          </svg>
        );
      case 4:
        return (
          <svg {...common}>
            <rect x="20" y="20" width="100" height="100" rx="12" stroke="currentColor" strokeWidth="4" fill="none"/>
            <path d="M20 20 L30 30 M120 20 L110 30 M20 120 L30 110 M120 120 L110 110" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <rect x="35" y="35" width="70" height="70" rx="6" stroke="currentColor" strokeWidth="3" fill="none"/>
            <path d="M45 55h50M45 70h50M45 85h40" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <path d="M80 85l8 8 16-16" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          </svg>
        );
      default:
        return null;
    }
  };

  const steps = [
    {
      step: 1,
      title: 'Comprehensive Inventory Assessment',
      description: 'Evaluate excess inventory including condition, market value, and optimal disposition strategy to maximize recovery potential.',
    },
    {
      step: 2,
      title: 'Market Analysis & Data-Driven Pricing',
      description: 'Leverage market intelligence and real-time pricing data to determine optimal pricing and identify highest-value channels.',
    },
    {
      step: 3,
      title: 'Strategic Multi-Channel Routing',
      description: 'Route inventory to optimal channels: primary markets, secondary markets, liquidation, or certified recycling based on value potential.',
    },
    {
      step: 4,
      title: 'Execution & Financial Settlement',
      description: 'Complete logistics, sales execution, and financial settlement with detailed reporting and documentation for your records.',
    },
  ];

  const goToStep = (index) => {
    setCurrentStep(index);
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        'transform-inventory',
        'key-benefits',
        'industry-solutions',
        'documentation'
      ];
      
      const scrollPosition = window.scrollY + 150;
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setActiveSection(sectionId);
    }
  };

  return (
    <main className="services">
      <div className="container">
        <div className="sv-content-wrapper">
          <div className="sv-main-content">
            {/* Hero */}
            <section className="sv-hero">
              <div className="sv-eyebrow">Excess Inventory Liquidation</div>
              <h1 className="sv-title">Excess Inventory Liquidation & Optimization</h1>
              <p className="sv-subtitle">Free up space and cash by routing surplus or seasonal inventory to the highest-value channels with data-driven pricing and global buyers.</p>
            </section>

            {/* 4-Step Process Carousel */}
            <section className="ti-steps-carousel">
              <div className="ti-carousel-slide-container">
                <div 
                  className="ti-carousel-slides" 
                  style={{ transform: `translateX(-${currentStep * 100}%)` }}
                >
                  {steps.map((step, index) => (
                    <div key={step.step} className="ti-carousel-slide" style={slideBgStyle}>
                      <div className="ti-carousel-icon">
                        <StepIcon step={step.step} />
                      </div>
                      <div className="ti-carousel-content">
                        <div className="ti-step-badge">STEP {step.step}</div>
                        <h3>{step.title}</h3>
                        <p>{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="ti-carousel-indicators">
                {steps.map((_, index) => (
                  <button
                    key={index}
                    className={`ti-carousel-indicator ${index === currentStep ? 'active' : ''}`}
                    onClick={() => goToStep(index)}
                    aria-label={`Go to step ${index + 1}`}
                  />
                ))}
              </div>
            </section>

            {/* Main Content Section */}
            <section id="transform-inventory" className="sv-overview">
              <h2 className="sv-section-title">Transform Excess Inventory Into Working Capital</h2>
              <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
                Excess inventory ties up capital, consumes valuable warehouse space, and creates ongoing carrying costs. Seasonal overstock, discontinued lines, and slow-moving SKUs represent significant opportunity cost when left unaddressed. Our comprehensive excess inventory liquidation service transforms this challenge into an opportunity to free up space and convert inventory into working capital.
              </p>
              <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
                Our data-driven approach leverages market intelligence and real-time pricing data to route inventory to the highest-value channels. We maintain brand protection through strategic market routing and MAP compliance while maximizing recovery value through our global buyer network of liquidators, wholesalers, retailers, and international markets.
              </p>
            </section>

            {/* Key Benefits */}
            <section id="key-benefits" className="sv-features">
              <h2 className="sv-section-title">Key Benefits</h2>
              <div className="sv-features-grid">
                <div className="sv-feature">
                  <h4>Data-Driven Pricing</h4>
                  <p>Leverage market intelligence and real-time pricing data to maximize recovery value across multiple channels and buyer networks.</p>
                </div>
                <div className="sv-feature">
                  <h4>Global Buyer Network</h4>
                  <p>Access to extensive network of qualified buyers including liquidators, wholesalers, retailers, and international markets.</p>
                </div>
                <div className="sv-feature">
                  <h4>Rapid Turnaround</h4>
                  <p>Fast processing and liquidation to free up warehouse space quickly and convert inventory into working capital.</p>
                </div>
                <div className="sv-feature">
                  <h4>Brand Protection</h4>
                  <p>Strategic channel routing and MAP compliance to protect brand reputation while maximizing recovery value.</p>
                </div>
              </div>
            </section>

            {/* Industry Solutions */}
            <section id="industry-solutions" className="sv-overview">
              <h2 className="sv-section-title">Industry-Specific Solutions</h2>
              <ul className="sv-bullets sv-list">
                <li>Retail – Clear seasonal overstock, discontinued lines, and excess merchandise</li>
                <li>Ecommerce – Liquidate slow-moving SKUs and warehouse overflow</li>
                <li>Manufacturing – Dispose of excess raw materials, components, and finished goods</li>
                <li>Wholesale – Route bulk excess inventory to secondary markets and liquidators</li>
                <li>Consumer Goods – Process overstock across multiple product categories</li>
              </ul>
            </section>

            {/* Documentation */}
            <section id="documentation" className="sv-overview">
              <h2 className="sv-section-title">Complete Documentation & Reporting</h2>
              <ul className="sv-bullets sv-list">
                <li>Inventory Assessment Reports</li>
                <li>Market Analysis & Pricing Documentation</li>
                <li>Channel Routing Analytics</li>
                <li>Financial Settlement Statements</li>
                <li>Brand Compliance Certificates</li>
                <li>Environmental Impact Reports</li>
              </ul>
            </section>

            {/* Four Pillars */}
            <FourPillars />

            {/* CTA */}
            <section className="sv-cta">
              <div className="sv-cta-inner">
                <h3>Ready to liquidate your excessive inventory?</h3>
                <p>Get a free assessment and discover how we can free up space and recover value.</p>
                <div className="sv-cta-actions">
                  <a className="btn btn-primary" href="tel:+18778739336">Call (877) 873-9336</a>
                  <a className="btn btn-secondary" href="mailto:info@globoflo.com">Email Us</a>
                </div>
              </div>
            </section>
          </div>

          {/* Quick Links Sidebar */}
          <aside className="sv-quick-links">
            <h3 className="sv-quick-links-title">Excess Inventory Liquidation & Optimization</h3>
            <nav className="sv-quick-links-nav">
              <ul>
                <li>
                  <button 
                    className={activeSection === 'transform-inventory' ? 'active' : ''}
                    onClick={() => scrollToSection('transform-inventory')}
                  >
                    Transform Excess Inventory Into Working Capital
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'key-benefits' ? 'active' : ''}
                    onClick={() => scrollToSection('key-benefits')}
                  >
                    Key Benefits
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'industry-solutions' ? 'active' : ''}
                    onClick={() => scrollToSection('industry-solutions')}
                  >
                    Industry-Specific Solutions
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'documentation' ? 'active' : ''}
                    onClick={() => scrollToSection('documentation')}
                  >
                    Complete Documentation & Reporting
                  </button>
                </li>
              </ul>
            </nav>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default ExcessiveInventoryPage;
